import { CDN_BASE, CDN_VERSIONS } from './versions.js';

// Type imports (no runtime overhead)
import type { Terminal, ITerminalOptions } from 'xterm';
import type { FitAddon } from 'xterm-addon-fit';
import type { HLJSApi } from 'highlight.js';
import type {
  createChart as createChartType,
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  MouseEventParams,
  DeepPartial,
  TimeChartOptions,
} from 'lightweight-charts';
import type { Editor, EditorOptions } from '@tiptap/core';
import type { StarterKitOptions } from '@tiptap/starter-kit';
import type { UnderlineOptions } from '@tiptap/extension-underline';
import type { TextAlignOptions } from '@tiptap/extension-text-align';
import type { LinkOptions } from '@tiptap/extension-link';

/**
 * Bundle type for xterm and its addons
 */
export interface IXtermBundle {
  Terminal: typeof Terminal;
}

/**
 * Bundle type for xterm-addon-fit
 */
export interface IXtermFitAddonBundle {
  FitAddon: typeof FitAddon;
}

/**
 * Bundle type for xterm-addon-search
 * SearchAddon is loaded from CDN, so we use a minimal interface
 */
export interface IXtermSearchAddonBundle {
  SearchAddon: new () => IXtermSearchAddon;
}

/**
 * Minimal interface for xterm SearchAddon
 */
export interface IXtermSearchAddon {
  activate(terminal: Terminal): void;
  dispose(): void;
  findNext(term: string, searchOptions?: { regex?: boolean; wholeWord?: boolean; caseSensitive?: boolean; incremental?: boolean }): boolean;
  findPrevious(term: string, searchOptions?: { regex?: boolean; wholeWord?: boolean; caseSensitive?: boolean; incremental?: boolean }): boolean;
}

/**
 * Bundle type for TradingView Lightweight Charts
 */
export interface ILightweightChartsBundle {
  createChart: typeof createChartType;
  AreaSeries: any;
  LineSeries: any;
  ColorType: {
    Solid: string;
    VerticalGradient: string;
  };
  LineType: {
    Simple: number;
    WithSteps: number;
    Curved: number;
  };
  CrosshairMode: {
    Normal: number;
    Magnet: number;
    Hidden: number;
  };
}

/**
 * Minimal type for an ECharts instance (loaded from CDN)
 */
export interface IEchartsInstance {
  setOption(option: Record<string, any>, notMerge?: boolean): void;
  resize(opts?: { width?: number; height?: number }): void;
  dispose(): void;
  on(eventName: string, handler: (...args: any[]) => void): void;
  off(eventName: string, handler?: (...args: any[]) => void): void;
  getOption(): Record<string, any>;
  clear(): void;
}

/**
 * Bundle type for Apache ECharts
 */
export interface IEchartsBundle {
  init: (dom: HTMLElement, theme?: string | object | null, opts?: Record<string, any>) => IEchartsInstance;
  dispose: (chart: IEchartsInstance | HTMLElement | string) => void;
  getInstanceByDom: (dom: HTMLElement) => IEchartsInstance | undefined;
}

/**
 * Bundle type for Tiptap editor and extensions
 */
export interface ITiptapBundle {
  Editor: typeof Editor;
  StarterKit: { configure: (options?: Partial<StarterKitOptions>) => any };
  Underline: { configure: (options?: Partial<UnderlineOptions>) => any };
  TextAlign: { configure: (options?: Partial<TextAlignOptions>) => any };
  Link: { configure: (options?: Partial<LinkOptions>) => any };
  Typography: { configure: (options?: any) => any };
}

/**
 * Singleton service for lazy-loading heavy libraries from CDN.
 *
 * This reduces initial bundle size by loading libraries only when needed.
 * Libraries are cached after first load to avoid duplicate fetches.
 *
 * @example
 * ```typescript
 * const libLoader = DeesServiceLibLoader.getInstance();
 * const xterm = await libLoader.loadXterm();
 * const terminal = new xterm.Terminal({ ... });
 * ```
 */
// Fix for xterm.js WidthCache measurement container causing horizontal
// scrollbar: xterm creates it on document.body with width 50000px/top
// -50000px; moving it off-screen horizontally prevents scrollWidth expansion.
const xtermMeasurementFixCss = `
/* Fix xterm.js WidthCache measurement container causing horizontal scrollbar */
body > div[style*="top: -50000px"][style*="width: 50000px"] {
  left: -50000px !important;
}
`;

export class DeesServiceLibLoader {
  private static instance: DeesServiceLibLoader;

  // Cached library references
  private xtermLib: IXtermBundle | null = null;
  private xtermFitAddonLib: IXtermFitAddonBundle | null = null;
  private xtermSearchAddonLib: IXtermSearchAddonBundle | null = null;
  private highlightJsLib: HLJSApi | null = null;
  private lightweightChartsLib: ILightweightChartsBundle | null = null;
  private echartsLib: IEchartsBundle | null = null;
  private tiptapLib: ITiptapBundle | null = null;

  // Loading promises to prevent duplicate concurrent loads
  private xtermLoadingPromise: Promise<IXtermBundle> | null = null;
  private xtermFitAddonLoadingPromise: Promise<IXtermFitAddonBundle> | null = null;
  private xtermSearchAddonLoadingPromise: Promise<IXtermSearchAddonBundle> | null = null;
  private highlightJsLoadingPromise: Promise<HLJSApi> | null = null;
  private lightweightChartsLoadingPromise: Promise<ILightweightChartsBundle> | null = null;
  private echartsLoadingPromise: Promise<IEchartsBundle> | null = null;
  private tiptapLoadingPromise: Promise<ITiptapBundle> | null = null;

  private constructor() {}

  /**
   * Get the singleton instance of DeesServiceLibLoader
   */
  public static getInstance(): DeesServiceLibLoader {
    if (!DeesServiceLibLoader.instance) {
      DeesServiceLibLoader.instance = new DeesServiceLibLoader();
    }
    return DeesServiceLibLoader.instance;
  }

  /**
   * Load xterm terminal emulator from CDN
   * @returns Promise resolving to xterm module with Terminal class
   */
  public async loadXterm(): Promise<IXtermBundle> {
    if (this.xtermLib) {
      return this.xtermLib;
    }

    if (this.xtermLoadingPromise) {
      return this.xtermLoadingPromise;
    }

    this.xtermLoadingPromise = (async () => {
      const url = `${CDN_BASE}/xterm@${CDN_VERSIONS.xterm}/+esm`;
      const module = await import(/* @vite-ignore */ url);

      // Also load and inject xterm CSS
      await this.injectXtermStyles();

      this.xtermLib = {
        Terminal: module.Terminal,
      };
      return this.xtermLib;
    })();

    return this.xtermLoadingPromise;
  }

  /**
   * Provides xterm modules from the host application instead of the CDN.
   * Hosts that bundle xterm (e.g. Electron apps that must work offline)
   * call this before any terminal component initializes; subsequent
   * loadXterm/loadXtermFitAddon calls resolve from these caches without
   * network access. Pass the css text of xterm.css so the document-level
   * styles (normally fetched from the CDN) are injected too.
   */
  public provideXtermModules(input: {
    xterm: IXtermBundle;
    fitAddon?: IXtermFitAddonBundle;
    cssText?: string;
  }): void {
    this.xtermLib = { Terminal: input.xterm.Terminal };
    if (input.fitAddon) {
      this.xtermFitAddonLib = { FitAddon: input.fitAddon.FitAddon };
    }
    if (input.cssText) {
      this.injectProvidedXtermStyles(input.cssText);
    }
  }

  private injectProvidedXtermStyles(cssText: string): void {
    const styleId = 'xterm-cdn-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = cssText + xtermMeasurementFixCss;
    document.head.appendChild(style);
  }

  /**
   * Load xterm-addon-fit from CDN
   * @returns Promise resolving to FitAddon class
   */
  public async loadXtermFitAddon(): Promise<IXtermFitAddonBundle> {
    if (this.xtermFitAddonLib) {
      return this.xtermFitAddonLib;
    }

    if (this.xtermFitAddonLoadingPromise) {
      return this.xtermFitAddonLoadingPromise;
    }

    this.xtermFitAddonLoadingPromise = (async () => {
      const url = `${CDN_BASE}/xterm-addon-fit@${CDN_VERSIONS.xtermAddonFit}/+esm`;
      const module = await import(/* @vite-ignore */ url);

      this.xtermFitAddonLib = {
        FitAddon: module.FitAddon,
      };
      return this.xtermFitAddonLib;
    })();

    return this.xtermFitAddonLoadingPromise;
  }

  /**
   * Load xterm-addon-search from CDN
   * @returns Promise resolving to SearchAddon class
   */
  public async loadXtermSearchAddon(): Promise<IXtermSearchAddonBundle> {
    if (this.xtermSearchAddonLib) {
      return this.xtermSearchAddonLib;
    }

    if (this.xtermSearchAddonLoadingPromise) {
      return this.xtermSearchAddonLoadingPromise;
    }

    this.xtermSearchAddonLoadingPromise = (async () => {
      const url = `${CDN_BASE}/xterm-addon-search@${CDN_VERSIONS.xtermAddonSearch}/+esm`;
      const module = await import(/* @vite-ignore */ url);

      this.xtermSearchAddonLib = {
        SearchAddon: module.SearchAddon,
      };
      return this.xtermSearchAddonLib;
    })();

    return this.xtermSearchAddonLoadingPromise;
  }

  /**
   * Inject xterm CSS styles into the document head
   */
  private async injectXtermStyles(): Promise<void> {
    const styleId = 'xterm-cdn-styles';
    if (document.getElementById(styleId)) {
      return; // Already injected
    }

    const cssUrl = `${CDN_BASE}/xterm@${CDN_VERSIONS.xterm}/css/xterm.css`;
    const response = await fetch(cssUrl);
    const cssText = await response.text();

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = cssText + xtermMeasurementFixCss;
    document.head.appendChild(style);
  }

  /**
   * Load highlight.js syntax highlighter from CDN
   * @returns Promise resolving to highlight.js API
   */
  public async loadHighlightJs(): Promise<HLJSApi> {
    if (this.highlightJsLib) {
      return this.highlightJsLib;
    }

    if (this.highlightJsLoadingPromise) {
      return this.highlightJsLoadingPromise;
    }

    this.highlightJsLoadingPromise = (async () => {
      const url = `${CDN_BASE}/highlight.js@${CDN_VERSIONS.highlightJs}/+esm`;
      const module = await import(/* @vite-ignore */ url);

      this.highlightJsLib = module.default;
      return this.highlightJsLib!;
    })();

    return this.highlightJsLoadingPromise;
  }

  /**
   * Load TradingView Lightweight Charts from CDN
   * @returns Promise resolving to Lightweight Charts bundle
   */
  public async loadLightweightCharts(): Promise<ILightweightChartsBundle> {
    if (this.lightweightChartsLib) {
      return this.lightweightChartsLib;
    }

    if (this.lightweightChartsLoadingPromise) {
      return this.lightweightChartsLoadingPromise;
    }

    this.lightweightChartsLoadingPromise = (async () => {
      const url = `${CDN_BASE}/lightweight-charts@${CDN_VERSIONS.lightweightCharts}/+esm`;
      const module = await import(/* @vite-ignore */ url);

      this.lightweightChartsLib = {
        createChart: module.createChart,
        AreaSeries: module.AreaSeries,
        LineSeries: module.LineSeries,
        ColorType: module.ColorType,
        LineType: module.LineType,
        CrosshairMode: module.CrosshairMode,
      };
      return this.lightweightChartsLib;
    })();

    return this.lightweightChartsLoadingPromise;
  }

  /**
   * Load Apache ECharts from CDN
   * @returns Promise resolving to ECharts bundle
   */
  public async loadEcharts(): Promise<IEchartsBundle> {
    if (this.echartsLib) {
      return this.echartsLib;
    }

    if (this.echartsLoadingPromise) {
      return this.echartsLoadingPromise;
    }

    this.echartsLoadingPromise = (async () => {
      // Use the pre-built ESM bundle which includes all chart types and components.
      // The +esm wrapper only exports the core without auto-registered chart types.
      const url = `${CDN_BASE}/echarts@${CDN_VERSIONS.echarts}/dist/echarts.esm.min.js`;
      const module = await import(/* @vite-ignore */ url);

      this.echartsLib = {
        init: module.init,
        dispose: module.dispose,
        getInstanceByDom: module.getInstanceByDom,
      };
      return this.echartsLib;
    })();

    return this.echartsLoadingPromise;
  }

  /**
   * Load Tiptap rich text editor and extensions from CDN
   * @returns Promise resolving to Tiptap bundle with Editor and extensions
   */
  public async loadTiptap(): Promise<ITiptapBundle> {
    if (this.tiptapLib) {
      return this.tiptapLib;
    }

    if (this.tiptapLoadingPromise) {
      return this.tiptapLoadingPromise;
    }

    this.tiptapLoadingPromise = (async () => {
      // Import directly from npm packages — bundled by esbuild into a single
      // module graph. CDN loading caused duplicate ProseMirror instances because
      // jsdelivr resolves each package's dependencies independently.
      const [coreModule, starterKitModule, underlineModule, textAlignModule, linkModule, typographyModule] = await Promise.all([
        import('@tiptap/core'),
        import('@tiptap/starter-kit'),
        import('@tiptap/extension-underline'),
        import('@tiptap/extension-text-align'),
        import('@tiptap/extension-link'),
        import('@tiptap/extension-typography'),
      ]);

      this.tiptapLib = {
        Editor: coreModule.Editor,
        StarterKit: starterKitModule.default || starterKitModule.StarterKit,
        Underline: underlineModule.default || underlineModule.Underline,
        TextAlign: textAlignModule.default || textAlignModule.TextAlign,
        Link: linkModule.default || linkModule.Link,
        Typography: typographyModule.default || typographyModule.Typography,
      };

      return this.tiptapLib;
    })();

    return this.tiptapLoadingPromise;
  }

  /**
   * Preload multiple libraries in parallel
   * Useful for warming the cache before components are rendered
   */
  public async preloadAll(): Promise<void> {
    await Promise.all([
      this.loadXterm(),
      this.loadXtermFitAddon(),
      this.loadXtermSearchAddon(),
      this.loadHighlightJs(),
      this.loadLightweightCharts(),
      this.loadEcharts(),
      this.loadTiptap(),
    ]);
  }

  /**
   * Check if a specific library is already loaded
   */
  public isLoaded(library: 'xterm' | 'xtermFitAddon' | 'xtermSearchAddon' | 'highlightJs' | 'lightweightCharts' | 'echarts' | 'tiptap'): boolean {
    switch (library) {
      case 'xterm':
        return this.xtermLib !== null;
      case 'xtermFitAddon':
        return this.xtermFitAddonLib !== null;
      case 'xtermSearchAddon':
        return this.xtermSearchAddonLib !== null;
      case 'highlightJs':
        return this.highlightJsLib !== null;
      case 'lightweightCharts':
        return this.lightweightChartsLib !== null;
      case 'echarts':
        return this.echartsLib !== null;
      case 'tiptap':
        return this.tiptapLib !== null;
      default:
        return false;
    }
  }
}
